import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import { useRouter } from 'next/router'
import ErrorBoundary from '@components/error/boundary'
import StorySpotlightWithData from '@components/story-spotlight/story-spotlight.query'
import defaults from '@config/defaults'
import { NextSeo } from 'next-seo'
import RelevantArticles from '@components/relevant-articles'
import PartnerLogos from '@components/partner-logos'
import Hero from '@components/hero-new/hero'
import Hidden from '@material-ui/core/Hidden'
import UpcomingFeaturedEvents from '@components/upcoming-featured-events/index'
import { MasonryGridQuery } from '@components/masonry-grid/index'
import { LeaderboardQuery } from '@components/leaderboard/index'
import LeaderboardHeadline from '@components/leaderboard/leaderboard-headline'
import TestimonialV2 from '@components/testimonialv2'
import MarketingFormEmbed from '@components/marketing-form-embed'
import useStyles from './program.styles'
import { ACTIVITIES_QUERY, FEATURED_STORIES_QUERY } from './program.fragment'

const Program = (props) => {
  const {
    slug,
    showEventsListing,
    query,
    hero,
    heroSecondaryCtaText,
    heroSecondaryCtaLink,
    promotedLinksCollection,
    relevantStoriesHeadline,
    relevantStoriesCollection,
    relevantStoriesCtaText,
    relevantStoriesCtaLink,
    testimonialsSection,
    partnerLogosCollection,
    newsletterSignup,
    metaTitle,
    metaDescription,
    metaImage,
    leaderboardsCollection,
  } = props
  const styles = useStyles()
  const router = useRouter()

  const hasPartnerLogos = !!partnerLogosCollection && !!partnerLogosCollection.total
  const hasRelevantStories = !!relevantStoriesCollection && !!relevantStoriesCollection.total
  if (!router) return null

  return (
    <div className={styles.other}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={metaTitle}
        description={metaDescription}
        canonical={`${defaults.baseUrl}pga-of-america/${slug}`}
        openGraph={{
          title: metaTitle,
          type: 'article',
          url: `${defaults.baseUrl}${router.asPath.substring(1)}`, // trimming string because of /
          images: [{ url: metaImage && metaImage.url }],
        }}
      />
      <Box>
        {!!hero && (
          <ErrorBoundary>
            <Hero
              hero={hero}
              ctas={promotedLinksCollection}
              secondaryCtaLink={heroSecondaryCtaLink}
              secondaryCtaText={heroSecondaryCtaText}
            />
          </ErrorBoundary>
        )}
        <ErrorBoundary>
          <Box py={4}>
            {leaderboardsCollection.items && leaderboardsCollection.items.map(item => (
              item.tournamentId && item.feedId && (
                <div className={styles.leaderboardContainer} key={item.tournamentId}>
                  <LeaderboardHeadline {...item} />
                  <LeaderboardQuery {...item} id={item.tournamentId} limit={Number(item.leaderboardPlaces) || null} enableScorecard />
                </div>
              )
            ))}
          </Box>
        </ErrorBoundary>
        <ErrorBoundary>
          <Box className={styles.spotlightWrapper}>
            <StorySpotlightWithData
              graphqlQuery={FEATURED_STORIES_QUERY}
              parentCollection="promotionalPageCollection"
              variables={{ slug: query.slug }}
              query={query}
            />
          </Box>
        </ErrorBoundary>
        {!!showEventsListing && (
          <ErrorBoundary>
            <UpcomingFeaturedEvents {...props} />
          </ErrorBoundary>
        )}
        <ErrorBoundary>
          <Box>
            <MasonryGridQuery
              graphqlQuery={ACTIVITIES_QUERY}
              parentCollection="promotionalPageCollection"
              variables={{ slug: query.slug }}
              query={query}
            />
          </Box>
        </ErrorBoundary>
        {!!testimonialsSection && (
          <ErrorBoundary>
            <Hidden smDown>
              <TestimonialV2 testimonialsData={testimonialsSection} />
            </Hidden>
          </ErrorBoundary>
        )}
        {!!hasRelevantStories && (
          <ErrorBoundary>
            <Box bgcolor="white" className={styles.articlesWrapper}>
              <RelevantArticles
                parentCollection="promotionalPageCollection"
                headline={relevantStoriesHeadline}
                storyCollection={relevantStoriesCollection}
                ctaText={relevantStoriesCtaText}
                link={relevantStoriesCtaLink}
              />
            </Box>
          </ErrorBoundary>
        )}
        {!!hasPartnerLogos && (
          <ErrorBoundary>
            <Box className={styles.sliderContainer}>
              <PartnerLogos
                logoCollection={partnerLogosCollection}
              />
            </Box>
          </ErrorBoundary>
        )}
        {!!newsletterSignup && (
          <ErrorBoundary>
            <MarketingFormEmbed {...newsletterSignup} />
          </ErrorBoundary>
        )}
      </Box>
    </div>
  )
}

Program.propTypes = {
  sys: PropTypes.shape({
    id: PropTypes.string,
  }),
  showEventsListing: PropTypes.bool,
  slug: PropTypes.string,
  hero: PropTypes.object,
  heroSecondaryCtaText: PropTypes.string,
  heroSecondaryCtaLink: PropTypes.string,
  promotedLinksCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  featuredStoriesCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  featuredStoriesHeadline: PropTypes.string,
  featuredStoriesCtaText: PropTypes.string,
  featuredStoriesCtaLink: PropTypes.string,
  activitiesHeadline: PropTypes.string,
  activitiesSummary: PropTypes.string,
  activitiesCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        headline: PropTypes.string,
        caption: PropTypes.string,
        image: PropTypes.shape({
          url: PropTypes.string,
          description: PropTypes.string,
        }),
      }),
    ),
  }),
  relevantStoriesHeadline: PropTypes.string,
  relevantStoriesCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  relevantStoriesCtaText: PropTypes.string,
  relevantStoriesCtaLink: PropTypes.string,
  testimonialsSection: PropTypes.object,
  partnerLogosCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    ),
  }),
  newsletterSignup: PropTypes.shape({
    headline: PropTypes.string,
    digiohFormId: PropTypes.string,
    background: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string,
  metaImage: PropTypes.shape({
    url: PropTypes.string,
  }),
  query: PropTypes.object,
  leaderboardsCollection: PropTypes.shape({
    items: PropTypes.array,
  }),
}

Program.defaultProps = {
  showEventsListing: false,
}

export default Program
